import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/app/AuthProvider';
import { QuickbooksPageShell } from './QuickbooksPageShell';

/**
 * Public landing page Intuit redirects users to when they click "Launch"
 * on the Atlas listing inside QuickBooks Online's My Apps page.
 *
 * Registered with Intuit as the **Launch URL** (App Settings →
 * Production / Development → "Launch URL"). Must be reachable without
 * Atlas authentication so the Intuit reviewer can fetch it.
 *
 * Behavior:
 *   - If the user is already signed in to Atlas → bounce them straight
 *     into the app at /dashboard (the canonical post-auth landing).
 *   - If they're not signed in → render a sign-in CTA. After signing
 *     in they end up on /dashboard via the standard sign-in redirect.
 */
export function QuickbooksLaunchPage() {
  const { session, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && session) {
      navigate('/dashboard', { replace: true });
    }
  }, [loading, session, navigate]);

  return (
    <QuickbooksPageShell
      eyebrow="QuickBooks Online · Launch"
      title="Welcome back to Atlas"
      description={
        <p>
          You&rsquo;re launching <strong>Atlas</strong> from QuickBooks
          Online. Atlas is the internal ERP for Virro — sign in with your
          Virro Google Workspace account to continue to your dashboard.
        </p>
      }
    >
      <div className="flex flex-wrap gap-3">
        <Button onClick={() => navigate('/auth/sign-in')}>
          Sign in to Atlas
        </Button>
        <Button variant="outline" onClick={() => navigate('/integrations')}>
          Go to Integrations
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Don&rsquo;t have an Atlas account? Atlas is an internal Virro tool;
        contact your Virro administrator for access.
      </p>
    </QuickbooksPageShell>
  );
}
